import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomModal from "./CustomModal";
import { AdminApis } from "../../apis/adminApi";
import {
  FaArrowLeft,
  FaImage,
  FaStore,
  FaSave,
  FaSpinner,
  FaCheckCircle,
  FaTimesCircle,
  FaExternalLinkAlt,
  FaPlus,
} from "react-icons/fa";
import { IoIosAddCircle, IoMdClose } from "react-icons/io";
import { Oval } from "react-loader-spinner";

export default function CardEditProduct() {
  const navigate = useNavigate();
  const params = useParams();

  const [showCreateMarketLinkModal, setShowCreateMarketLinkModal] = useState(false);

  const [productForm, setProductForm] = useState({
    id: "",
    name: "",
    description: "",
    phoneNumber: "",
    discountedPrice: "",
    category: "",
    location: "",
    price: "",
    marketLinkId: "",   // "link_name id" format required by the form submit
  });

  const [marketLinkForm, setMarketLinkForm] = useState({
    name: "",
    available: null,
    checking: false,
  });

  const [productLinks, setProductLinks] = useState([]);
  const [whatsappLinks, setWhatsappLinks] = useState([]);

  const [productImages, setProductImages] = useState({
    image1: { file: "No selected file", preview: "", id: "" },
    image2: { file: "No selected file", preview: "", id: "" },
    image3: { file: "No selected file", preview: "", id: "" },
  });

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const categories = [
    { value: "all", label: "All Categories" },
    { value: "women fashion", label: "Women's Fashion" },
    { value: "men fashion", label: "Men's Fashion" },
    { value: "beauty&cosmetics", label: "Beauty and Cosmetics" },
    { value: "bags", label: "Bags" },
    { value: "sport/outdoor", label: "Sport/Outdoor" },
    { value: "home/kitchen", label: "Home/Kitchen" },
    { value: "shoes", label: "Shoes" },
    { value: "watches", label: "Watches" },
    { value: "keyboard & mice", label: "Keyboard & Mice" },
    { value: "laptops", label: "Laptops" },
    { value: "phones", label: "Phones" },
  ];

  const nigerianStates = [
    "Abuja FCT", "Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa",
    "Benue", "Borno", "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti", "Enugu",
    "Gombe", "Imo", "Jigawa", "Kaduna", "Kano", "Katsina", "Kebbi", "Kogi",
    "Kwara", "Lagos", "Nassarawa", "Niger", "Ogun", "Ondo", "Osun", "Oyo",
    "Plateau", "Rivers", "Sokoto", "Taraba", "Yobe", "Zamfara",
  ];

  useEffect(() => { fetchInitialData(); }, []);

  useEffect(() => {
    if (marketLinkForm.name.length > 0) {
      setMarketLinkForm((p) => ({ ...p, available: null, checking: true }));
      const id = setTimeout(checkLinkAvailability, 500);
      return () => clearTimeout(id);
    }
  }, [marketLinkForm.name]);

  /* ─── Data fetching ─────────────────────────────────────────────────── */
  const fetchInitialData = async () => {
    try {
      setInitialLoading(true);
      // Fetch stores + product in parallel; we need stores to resolve the
      // "link_name id" composite key the select uses.
      const [linksRes, productRes, waRes] = await Promise.all([
        AdminApis.getMarketLink(),
        AdminApis.getSingleProduct(params?.id),
        AdminApis.getlinks(),
      ]);

      const links = linksRes?.data?.link || [];
      setProductLinks(links);

      const wa = waRes?.data?.link || [];
      setWhatsappLinks(wa);

      if (productRes?.data?.data?.product) {
        const p = productRes.data.data.product;
        // Find the matching store so the select pre-selects correctly.
        const match = links.find((l) => l.link_name === p.link_name);
        setProductForm({
          id: p.id,
          name: p.product_name || "",
          description: p.product_description || "",
          phoneNumber: p.phone_number || "",
          discountedPrice: p.no_of_items || "",
          category: p.category || "",
          location: p.location || "",
          price: p.product_price || "",
          marketLinkId: match ? `${match.link_name} ${match.id}` : "",
        });
        setProductImages({
          image1: { file: p.product_image_1 || "No selected file", preview: p.product_image_1 || "", id: p.product_image_id_1 || "" },
          image2: { file: p.product_image_2 || "No selected file", preview: p.product_image_2 || "", id: p.product_image_id_2 || "" },
          image3: { file: p.product_image_3 || "No selected file", preview: p.product_image_3 || "", id: p.product_image_id_3 || "" },
        });
      }
    } catch {
      toast.error("Failed to load product data");
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchMarketLinks = async () => {
    try {
      const res = await AdminApis.getMarketLink();
      if (res?.data) setProductLinks(res.data.link || []);
    } catch {}
  };

  const checkLinkAvailability = async () => {
    try {
      const clean = marketLinkForm.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "");
      const res = await AdminApis.checkMarketLink({ link_name: clean });
      setMarketLinkForm((p) => ({ ...p, available: res?.data?.link === 0, checking: false }));
    } catch {
      setMarketLinkForm((p) => ({ ...p, available: false, checking: false }));
    }
  };

  const handleImageUpload = (e, key) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size / 1048576 > 4) {
      e.target.value = "";
      toast.warn(`Image too large (${(file.size / 1048576).toFixed(1)} MB). Max is 4 MB.`);
      return;
    }
    setProductImages((p) => ({ ...p, [key]: { ...p[key], file, preview: URL.createObjectURL(file) } }));
  };

  const handleProductFormChange = (field, value) =>
    setProductForm((p) => ({ ...p, [field]: value }));

  const handleMarketLinkFormChange = (field, value) =>
    setMarketLinkForm((p) => ({ ...p, [field]: value }));

  /* ─── API calls ─────────────────────────────────────────────────────── */
  const updateProduct = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      const [linkName, linkId] = productForm.marketLinkId.split(" ");
      fd.append("link_name", linkName);
      fd.append("link_id", linkId);
      fd.append("product_name", productForm.name);
      fd.append("product_description", productForm.description);
      fd.append("phone_number", productForm.phoneNumber);
      fd.append("no_of_items", productForm.discountedPrice);
      fd.append("product_price", productForm.price);
      fd.append("category", productForm.category);
      fd.append("location", productForm.location);
      fd.append("id", productForm.id);
      fd.append("product_image_1", productImages.image1.file);
      fd.append("product_image_2", productImages.image2.file);
      fd.append("product_image_3", productImages.image3.file);
      fd.append("product_image_id_1", productImages.image1.id);
      fd.append("product_image_id_2", productImages.image2.id);
      fd.append("product_image_id_3", productImages.image3.id);
      const res = await AdminApis.updateProduct(fd);
      if (res?.data) {
        toast.success("Product updated successfully");
        navigate("/mini-store");
      } else {
        toast.error(res?.response?.data?.message || "Failed to update product");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update product");
    } finally {
      setLoading(false);
    }
  };

  const createMarketLink = async (e) => {
    e.preventDefault();
    if (!marketLinkForm.available) { toast.error("Please choose an available link name"); return; }
    try {
      const fd = new FormData();
      fd.append("link_name", marketLinkForm.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, ""));
      const res = await AdminApis.createMarketLink(fd);
      if (res?.data) {
        toast.success(res.data.message);
        setShowCreateMarketLinkModal(false);
        setMarketLinkForm({ name: "", available: null, checking: false });
        fetchMarketLinks();
      } else {
        toast.error(res?.response?.data?.message || "Failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed");
    }
  };

  /* ─── Derived values ─────────────────────────────────────────────────── */
  const currentStoreName = productForm.marketLinkId ? productForm.marketLinkId.split(" ")[0] : null;
  const currentStore = productLinks.find((l) => l.link_name === currentStoreName);

  /* ─── Sub-components ─────────────────────────────────────────────────── */
  const ImageSlot = ({ imageKey, image, label, required = false }) => (
    <label className="group relative flex flex-col cursor-pointer">
      <div className={`relative overflow-hidden rounded-xl border-2 border-dashed transition-all duration-200 aspect-square ${
        image.preview && image.preview !== "no image"
          ? "border-blue-400 bg-white"
          : "border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/30"
      }`}>
        {image.preview && image.preview !== "no image" ? (
          <>
            <img src={image.preview} alt="preview" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setProductImages((p) => ({ ...p, [imageKey]: { ...p[imageKey], file: "No selected file", preview: "" } }));
              }}
              className="absolute top-1.5 right-1.5 w-6 h-6 bg-white/90 hover:bg-white rounded-full shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <IoMdClose size={13} className="text-gray-600" />
            </button>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-gray-400 group-hover:text-blue-500 transition-colors">
            <FaImage size={24} />
            <span className="text-xs font-medium">{label}{required && " *"}</span>
            <span className="text-[10px] text-gray-400">Max 4 MB</span>
          </div>
        )}
      </div>
      <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, imageKey)} />
    </label>
  );

  const SectionHeader = ({ number, title, subtitle }) => (
    <div className="flex items-start gap-3 mb-5">
      <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white text-sm font-bold rounded-xl flex items-center justify-center shadow-sm">
        {number}
      </div>
      <div>
        <h3 className="text-base font-bold text-gray-900 leading-tight">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );

  const FieldLabel = ({ children, required }) => (
    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
      {children}{required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );

  const inputCls = "w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all";

  /* ─── Loading skeleton ───────────────────────────────────────────────── */
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center">
          <Oval height={40} width={40} color="#3b82f6" secondaryColor="#bfdbfe" strokeWidth={3} />
          <p className="mt-4 text-sm text-gray-500 font-medium">Loading product details…</p>
        </div>
      </div>
    );
  }

  /* ─── Render ─────────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            type="button"
            onClick={() => navigate("/mini-store")}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-gray-800 hover:shadow-sm transition-all"
          >
            <FaArrowLeft size={14} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Edit Product</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Editing: <span className="font-semibold text-gray-700">{productForm.name || "—"}</span>
            </p>
          </div>
          {/* Quick link to live listing */}
          {currentStoreName && (
            <a
              href={`https://mygupta.co/s/${currentStoreName}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:text-blue-600 hover:border-blue-300 transition-all shadow-sm"
            >
              <FaExternalLinkAlt size={11} /> View Store
            </a>
          )}
        </div>

        {/* ── Live location banner (always visible) ── */}
        {currentStore && (
          <div className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3 mb-6">
            <div
              className="w-8 h-8 rounded-xl flex-shrink-0 flex items-center justify-center text-white"
              style={{ backgroundColor: currentStore.brand_primary_color || "#2563EB" }}
            >
              <FaStore size={13} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-blue-700">This product is listed on</p>
              <p className="text-sm font-bold text-blue-900 truncate">{currentStore.link_name}</p>
            </div>
            <a
              href={`https://mygupta.co/s/${currentStore.link_name}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
            >
              <FaExternalLinkAlt size={10} />
              mygupta.co/s/{currentStore.link_name}
            </a>
          </div>
        )}

        <form onSubmit={updateProduct}>
          <div className="grid lg:grid-cols-5 gap-6">

            {/* ── Left Column ────────────────────────────────────────── */}
            <div className="lg:col-span-3 space-y-5">

              {/* Section 1 – Store & Contact (prerequisite, shown first) */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <SectionHeader
                  number="1"
                  title="Store & Contact"
                  subtitle="The store this product appears in, and the WhatsApp number customers use to buy"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Store */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <FieldLabel required>Market Store</FieldLabel>
                      <button
                        type="button"
                        onClick={() => setShowCreateMarketLinkModal(true)}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-semibold"
                      >
                        <IoIosAddCircle size={15} /> New store
                      </button>
                    </div>
                    {productLinks.length === 0 ? (
                      <button
                        type="button"
                        onClick={() => setShowCreateMarketLinkModal(true)}
                        className="w-full flex flex-col items-center justify-center gap-1.5 py-5 border-2 border-dashed border-blue-300 bg-blue-50 rounded-xl text-blue-600 hover:bg-blue-100 transition-colors"
                      >
                        <FaStore size={22} className="text-blue-400" />
                        <span className="text-sm font-semibold">Create a store first</span>
                        <span className="text-xs text-blue-400">Products must belong to a store</span>
                      </button>
                    ) : (
                      <>
                        <select
                          required
                          value={productForm.marketLinkId}
                          onChange={(e) => handleProductFormChange("marketLinkId", e.target.value)}
                          className={inputCls}
                        >
                          <option value="">Select store…</option>
                          {productLinks.map((l) => (
                            <option key={l.id} value={`${l.link_name} ${l.id}`}>{l.link_name}</option>
                          ))}
                        </select>
                        {currentStoreName && (
                          <a
                            href={`https://mygupta.co/s/${currentStoreName}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-blue-500 hover:text-blue-700 mt-1.5 transition-colors"
                          >
                            <FaExternalLinkAlt size={9} />
                            mygupta.co/s/{currentStoreName}
                          </a>
                        )}
                      </>
                    )}
                  </div>

                  {/* WhatsApp */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <FieldLabel required>WhatsApp Contact</FieldLabel>
                      <Link to="/mylinks" className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-semibold">
                        <IoIosAddCircle size={15} /> Add link
                      </Link>
                    </div>
                    {whatsappLinks.length === 0 ? (
                      <Link
                        to="/mylinks"
                        className="w-full flex flex-col items-center justify-center gap-1.5 py-5 border-2 border-dashed border-green-300 bg-green-50 rounded-xl text-green-600 hover:bg-green-100 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24">
                          <path fill="currentColor" d="M19.05 4.91A9.816 9.816 0 0 0 12.04 2c-5.46 0-9.91 4.45-9.91 9.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21c5.46 0 9.91-4.45 9.91-9.91c0-2.65-1.03-5.14-2.9-7.01m-7.01 15.24c-1.48 0-2.93-.4-4.2-1.15l-.3-.18l-3.12.82l.83-3.04l-.2-.31a8.264 8.264 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24c2.2 0 4.27.86 5.82 2.42a8.183 8.183 0 0 1 2.41 5.83c.02 4.54-3.68 8.23-8.22 8.23m4.52-6.16c-.25-.12-1.47-.72-1.69-.81c-.23-.08-.39-.12-.56.12c-.17.25-.64.81-.78.97c-.14.17-.29.19-.54.06c-.25-.12-1.05-.39-1.99-1.23c-.74-.66-1.23-1.47-1.38-1.72c-.14-.25-.02-.38.11-.51c.11-.11.25-.29.37-.43s.17-.25.25-.41c.08-.17.04-.31-.02-.43s-.56-1.34-.76-1.84c-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31c-.22.25-.86.85-.86 2.07c0 1.22.89 2.4 1.01 2.56c.12.17 1.75 2.67 4.23 3.74c.59.26 1.05.41 1.41.52c.59.19 1.13.16 1.56.1c.48-.07 1.47-.6 1.67-1.18c.21-.58.21-1.07.14-1.18s-.22-.16-.47-.28" />
                        </svg>
                        <span className="text-sm font-semibold">Add WhatsApp contact</span>
                        <span className="text-xs text-green-400">Customers contact you here</span>
                      </Link>
                    ) : (
                      <select
                        required
                        value={productForm.phoneNumber}
                        onChange={(e) => handleProductFormChange("phoneNumber", e.target.value)}
                        className={inputCls}
                      >
                        <option value="">Select contact…</option>
                        {whatsappLinks.map((l) => (
                          <option key={l.id} value={l.name}>{l.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 2 – Product Info */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <SectionHeader number="2" title="Product Information" subtitle="Update the details visible to your customers" />
                <div className="space-y-4">
                  <div>
                    <FieldLabel required>Product Name</FieldLabel>
                    <input
                      type="text"
                      required
                      value={productForm.name}
                      onChange={(e) => handleProductFormChange("name", e.target.value)}
                      className={inputCls}
                      placeholder="e.g. Ladies Floral Dress"
                    />
                  </div>
                  <div>
                    <FieldLabel required>Description</FieldLabel>
                    <textarea
                      required
                      rows={4}
                      value={productForm.description}
                      onChange={(e) => handleProductFormChange("description", e.target.value)}
                      className={`${inputCls} resize-none`}
                      placeholder="Describe size, material, colour options, and key features…"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel>Category</FieldLabel>
                      <select value={productForm.category} onChange={(e) => handleProductFormChange("category", e.target.value)} className={inputCls}>
                        {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <FieldLabel>Location</FieldLabel>
                      <select value={productForm.location} onChange={(e) => handleProductFormChange("location", e.target.value)} className={inputCls}>
                        <option value="">Select state</option>
                        {nigerianStates.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 3 – Pricing */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <SectionHeader number="3" title="Pricing" subtitle="Update your regular and discounted prices" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel required>Sales Price (₦)</FieldLabel>
                    <input
                      type="number"
                      required
                      min="0"
                      value={productForm.price}
                      onChange={(e) => handleProductFormChange("price", e.target.value)}
                      className={inputCls}
                      placeholder="e.g. 5000"
                    />
                  </div>
                  <div>
                    <FieldLabel>
                      Discounted Price (₦){" "}
                      <span className="text-gray-400 font-normal text-xs">(optional)</span>
                    </FieldLabel>
                    <input
                      type="number"
                      min="0"
                      value={productForm.discountedPrice}
                      onChange={(e) => handleProductFormChange("discountedPrice", e.target.value)}
                      className={inputCls}
                      placeholder="e.g. 3500"
                    />
                  </div>
                </div>
                {productForm.price && productForm.discountedPrice &&
                  parseFloat(productForm.discountedPrice) < parseFloat(productForm.price) && (
                    <p className="mt-2.5 text-xs text-emerald-600 font-medium">
                      Showing a {Math.round((1 - parseFloat(productForm.discountedPrice) / parseFloat(productForm.price)) * 100)}% discount badge on your listing
                    </p>
                  )}
              </div>
            </div>

            {/* ── Right Column ────────────────────────────────────────── */}
            <div className="lg:col-span-2 space-y-5">

              {/* Section 4 – Images */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <SectionHeader number="4" title="Product Images" subtitle="Upload new images or keep the existing ones" />
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cover Image *</p>
                    <ImageSlot imageKey="image1" image={productImages.image1} label="Cover Photo" required />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: "image2", label: "Image 2" },
                      { key: "image3", label: "Image 3" },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{label}</p>
                        <ImageSlot imageKey={key} image={productImages[key]} label="Upload" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold text-sm rounded-2xl shadow-md hover:shadow-lg transition-all duration-200"
                >
                  {loading ? (
                    <><FaSpinner className="animate-spin" size={14} /> Saving…</>
                  ) : (
                    <><FaSave size={14} /> Save Changes</>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/mini-store")}
                  className="w-full py-3 text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-2xl transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* ── Create Market Link Modal ──────────────────────────────────── */}
      <CustomModal
        isOpen={showCreateMarketLinkModal}
        onClose={() => setShowCreateMarketLinkModal(false)}
        title="Create New Store"
        maxWidth="max-w-md"
      >
        <form onSubmit={createMarketLink} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Store URL Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={marketLinkForm.name}
              onChange={(e) => handleMarketLinkFormChange("name", e.target.value)}
              className={inputCls}
              placeholder="e.g. john-fashion"
            />
            <div className="flex items-center justify-between mt-1.5">
              <p className="text-[11px] text-gray-400">
                mygupta.co/s/{marketLinkForm.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "")}
              </p>
              {marketLinkForm.checking && <span className="text-xs text-gray-400 flex items-center gap-1"><FaSpinner className="animate-spin" size={10} /> Checking…</span>}
              {!marketLinkForm.checking && marketLinkForm.available === true && (
                <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium"><FaCheckCircle size={11} /> Available</span>
              )}
              {!marketLinkForm.checking && marketLinkForm.available === false && (
                <span className="flex items-center gap-1 text-xs text-red-500 font-medium"><FaTimesCircle size={11} /> Taken</span>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShowCreateMarketLinkModal(false)}
              className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!marketLinkForm.available}
              className={`px-5 py-2.5 text-sm font-semibold rounded-xl transition-colors flex items-center gap-2 ${
                marketLinkForm.available
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              <FaStore size={12} />
              {marketLinkForm.available ? "Create Store" : "Unavailable"}
            </button>
          </div>
        </form>
      </CustomModal>

      <ToastContainer position="top-right" autoClose={3000} theme="light" toastStyle={{ borderRadius: "12px" }} />
    </div>
  );
}
